import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  SxProps,
  TextField,
  Theme,
  Typography,
} from "@mui/material";
import {
  Control,
  Controller,
  ControllerRenderProps,
  FieldError,
  FieldValues,
  Path,
} from "react-hook-form";
import React, { ChangeEvent, memo, useCallback, useMemo, useState } from "react";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { DASH_FORMAT_DATE } from "constants/format";
import { TFieldType, TOption } from "type";
import LabelHeader from "../Label/LabelHeader";
import "dayjs/locale/ja";

// Improved TypeScript interfaces
interface BaseFormRowProps<T extends FieldValues = FieldValues> {
  label: string;
  sxLabel?: SxProps<Theme>;
  sxBoxField?: SxProps<Theme>;
  sxField?: SxProps<Theme>;
  sxBoxRow?: SxProps<Theme>;
  name: Path<T>;
  disabled?: boolean;
  checkboxLabel?: boolean;
  control: Control<T>;
  rules?: any;
  size?: "small" | "medium";
  onChange?: (value: any) => void;
}

interface TextFormRowProps<T extends FieldValues = FieldValues> extends BaseFormRowProps<T> {
  type?: TFieldType;
  options?: never;
  autoCompleteAllowAny?: never;
  formatDate?: never;
}

interface SelectFormRowProps<T extends FieldValues = FieldValues> extends BaseFormRowProps<T> {
  type: "select";
  options: TOption[];
  autoCompleteAllowAny?: never;
  formatDate?: never;
}

interface DateFormRowProps<T extends FieldValues = FieldValues> extends BaseFormRowProps<T> {
  type: "date";
  options?: never;
  autoCompleteAllowAny?: never;
  formatDate?: string;
}

interface AutocompleteFormRowProps<T extends FieldValues = FieldValues>
  extends BaseFormRowProps<T> {
  type: "autocomplete";
  options: TOption[];
  autoCompleteAllowAny?: boolean;
  formatDate?: never;
}

interface CheckboxFormRowProps<T extends FieldValues = FieldValues> extends BaseFormRowProps<T> {
  type: "checkbox";
  checkboxLabel?: boolean;
  options?: never;
  autoCompleteAllowAny?: never;
  formatDate?: never;
}

type FormRowProps<T extends FieldValues = FieldValues> =
  | TextFormRowProps<T>
  | SelectFormRowProps<T>
  | DateFormRowProps<T>
  | AutocompleteFormRowProps<T>
  | CheckboxFormRowProps<T>;

// Common field props interface
interface CommonFieldProps {
  field: ControllerRenderProps<FieldValues, any>;
  fieldState: { error?: FieldError };
  size?: "small" | "medium";
  disabled?: boolean;
  sxField?: SxProps<Theme>;
  onChange?: (value: any) => void;
}

// Specific field component props
interface SelectFieldProps extends CommonFieldProps {
  options: TOption[];
}

interface DateFieldProps extends CommonFieldProps {
  formatDate?: string;
}

interface AutocompleteFieldProps extends CommonFieldProps {
  options: TOption[];
  autoCompleteAllowAny?: boolean;
}

interface CheckboxFieldProps extends CommonFieldProps {
  label: string;
}

interface TextFieldProps extends CommonFieldProps {
  type?: "text" | "input" | "email" | "password" | "radio" | "number" | "number-float" | "label";
}

// Password Toggle Component
const PasswordToggle = memo(
  ({ showPassword, onToggle }: { showPassword: boolean; onToggle: () => void }) => (
    <InputAdornment position="end">
      <IconButton
        onClick={onToggle}
        edge="end"
        aria-label={showPassword ? "Hide password" : "Show password"}
        tabIndex={-1}
      >
        {showPassword ? <VisibilityOff /> : <Visibility />}
      </IconButton>
    </InputAdornment>
  ),
);

PasswordToggle.displayName = "PasswordToggle";

// Select Field Component
const SelectField = memo(
  ({ field, fieldState, options, size, disabled, sxField, onChange }: SelectFieldProps) => {
    const handleChange = useCallback(
      (event: SelectChangeEvent) => {
        field.onChange(event.target.value);
        onChange?.(event.target.value);
      },
      [field, onChange],
    );

    return (
      <FormControl fullWidth size={size} error={!!fieldState.error}>
        <Select
          value={field.value ?? ""}
          name={field.name}
          fullWidth
          disabled={disabled}
          size={size}
          error={!!fieldState.error}
          onChange={handleChange}
          onBlur={field.onBlur}
          aria-describedby={fieldState.error ? `${field.name}-error` : undefined}
          sx={{
            "& .MuiSelect-select": { padding: "12.5px 14px" },
            ...sxField,
            borderColor: fieldState.error ? "var(--red-800)" : "var(--silver-150)",
            backgroundColor: disabled ? "var(--offwhite-400)" : "var(--white)",
          }}
        >
          {options.map((opt: TOption) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  },
);

SelectField.displayName = "SelectField";

// Date Field Component
const DateField = memo(
  ({
    field,
    fieldState,
    size,
    disabled,
    sxField,
    formatDate = DASH_FORMAT_DATE,
    onChange,
  }: DateFieldProps) => {
    const handleDateChange = useCallback(
      (date: Dayjs | null) => {
        if (date && dayjs.isDayjs(date) && date.isValid()) {
          const convertDate = dayjs(date).format(formatDate);
          field.onChange(convertDate);
          onChange?.(convertDate);
        } else {
          field.onChange(null);
          onChange?.(null);
        }
      },
      [field, formatDate, onChange],
    );

    const dateValue = useMemo(() => (field.value ? dayjs(field.value) : null), [field.value]);

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ja">
        <DatePicker
          value={dateValue}
          onChange={handleDateChange}
          format={formatDate}
          minDate={dayjs().add(1, "day")}
          disabled={disabled}
          sx={{
            "& .MuiInputBase-input": { padding: "9.5px 14px" },
            ...(sxField as any),
            borderColor: fieldState.error ? "var(--red-800)" : "var(--silver-150)",
            backgroundColor: disabled ? "var(--offwhite-400)" : "var(--white)",
          }}
          slotProps={{
            textField: {
              size,
              error: !!fieldState.error,
              "aria-describedby": fieldState.error ? `${field.name}-error` : undefined,
            },
          }}
        />
      </LocalizationProvider>
    );
  },
);

DateField.displayName = "DateField";

// Autocomplete Field Component
const AutocompleteField = memo(
  ({
    field,
    fieldState,
    options = [],
    size,
    disabled,
    sxField,
    autoCompleteAllowAny = false,
    onChange,
  }: AutocompleteFieldProps) => {
    const initialValue = useMemo(() => {
      if (field.value === null || field.value === undefined) {
        return null;
      }

      if (field.value === "") {
        return autoCompleteAllowAny ? "" : null;
      }

      return (
        options.find(
          (opt: TOption) => opt.value === field.value?.value || opt.value === field.value,
        ) || {
          label: field.value?.label ?? String(field.value),
          value: field.value?.value ?? field.value,
        }
      );
    }, [field.value, options, autoCompleteAllowAny]);

    const handleAutocompleteChange = useCallback(
      (_event: any, suggest: any) => {
        field.onChange(suggest);
        onChange?.(suggest);
      },
      [field, onChange],
    );

    const handleInputChange = useCallback(
      (_event: any, newInputValue: string, reason: string) => {
        if (autoCompleteAllowAny && reason === "input") {
          field.onChange(newInputValue);
          onChange?.(newInputValue);
        }
      },
      [autoCompleteAllowAny, field, onChange],
    );

    const getOptionLabel = useCallback(
      (option: TOption | string) => (typeof option === "string" ? option : option.label),
      [],
    );

    const isOptionEqualToValue = useCallback(
      (option: TOption, value: TOption | null) => (value ? option.value === value.value : false),
      [],
    );

    return (
      <Autocomplete
        value={initialValue}
        fullWidth
        disabled={disabled}
        size={size}
        options={options}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={isOptionEqualToValue}
        freeSolo={autoCompleteAllowAny}
        noOptionsText="該当するデータが見つかりません"
        onChange={handleAutocompleteChange}
        onInputChange={handleInputChange}
        sx={{
          ...sxField,
          borderColor: fieldState.error ? "var(--red-800)" : "var(--silver-150)",
          backgroundColor: disabled ? "var(--offwhite-400)" : "var(--white)",
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size={size}
            error={!!fieldState.error}
            aria-describedby={fieldState.error ? `${field.name}-error` : undefined}
          />
        )}
      />
    );
  },
);

AutocompleteField.displayName = "AutocompleteField";

// Text Field Component
const TextFieldComponent = memo(
  ({ field, fieldState, type = "text", size, disabled, sxField, onChange }: TextFieldProps) => {
    const [showPassword, setShowPassword] = useState(false);
    const isPassword = type === "password";
    const isNumber = type === "number";
    const isNumberFloat = type === "number-float";

    const handleTogglePassword = useCallback(() => {
      setShowPassword((prev) => !prev);
    }, []);

    // Validation function for number inputs
    const validateNumberInput = useCallback(
      (value: string) => {
        if (isNumber) {
          // Only allow integers (0-9)
          return /^\d*$/.test(value);
        }
        if (isNumberFloat) {
          // Allow decimal numbers (0-9, decimal point)
          return /^\d*\.?\d*$/.test(value);
        }
        return true;
      },
      [isNumber, isNumberFloat],
    );

    const handleChange = useCallback(
      (event: any) => {
        const inputValue = event.target.value;

        // Validate for number types
        if ((isNumber || isNumberFloat) && !validateNumberInput(inputValue)) {
          return; // Don't allow invalid input values
        }

        field.onChange(event);
        onChange?.(event.target.value);
      },
      [field, onChange, isNumber, isNumberFloat, validateNumberInput],
    );

    const handleKeyPress = useCallback(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Safe check for event.key
        if (!event.key) return;

        if (isNumber) {
          // Only allow numbers (0-9), Backspace, Delete, Tab, Arrow keys
          const allowedKeys = [
            "Backspace",
            "Delete",
            "Tab",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
          ];
          if (!allowedKeys.includes(event.key) && !/^\d$/.test(event.key)) {
            event.preventDefault();
          }
        } else if (isNumberFloat) {
          // Allow numbers (0-9), decimal point, and control keys
          const allowedKeys = [
            "Backspace",
            "Delete",
            "Tab",
            "ArrowLeft",
            "ArrowRight",
            "ArrowUp",
            "ArrowDown",
            ".",
          ];
          const currentValue = event.currentTarget?.value || "";

          if (!allowedKeys.includes(event.key) && !/^\d$/.test(event.key)) {
            event.preventDefault();
          } else if (event.key === "." && currentValue.includes(".")) {
            // Don't allow multiple decimal points
            event.preventDefault();
          }
        }
      },
      [isNumber, isNumberFloat],
    );

    const inputType = useMemo(() => {
      if (isPassword) {
        return showPassword ? "text" : "password";
      }
      if (isNumber || isNumberFloat) {
        return "text"; // Use text type for better validation control
      }
      return type;
    }, [isPassword, showPassword, type, isNumber, isNumberFloat]);

    const inputProps = useMemo(() => {
      if (!isPassword) return undefined;

      return {
        endAdornment: (
          <PasswordToggle showPassword={showPassword} onToggle={handleTogglePassword} />
        ),
      };
    }, [isPassword, showPassword, handleTogglePassword]);

    return (
      <TextField
        {...field}
        type={inputType}
        fullWidth
        disabled={disabled}
        size={size}
        error={!!fieldState.error}
        onChange={handleChange}
        onKeyPress={handleKeyPress}
        aria-describedby={fieldState.error ? `${field.name}-error` : undefined}
        sx={{
          "& .MuiInputBase-input": { padding: "9.5px 14px" },
          ...sxField,
          borderColor: fieldState.error ? "var(--red-800)" : "var(--silver-150)",
          backgroundColor: disabled ? "var(--offwhite-400)" : "var(--white)",
        }}
        InputProps={inputProps}
      />
    );
  },
);

TextFieldComponent.displayName = "TextFieldComponent";

// Checkbox Field Component
const CheckboxField = memo(
  ({ field, fieldState, label, size, disabled, sxField, onChange }: CheckboxFieldProps) => {
    const handleChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        const { checked } = event.target;
        field.onChange(checked);
        onChange?.(checked);
      },
      [field, onChange],
    );

    return (
      <FormControlLabel
        control={
          <Checkbox
            {...field}
            checked={!!field.value}
            disabled={disabled}
            size={size}
            onChange={handleChange}
            aria-describedby={fieldState.error ? `${field.name}-error` : undefined}
            sx={{
              ...sxField,
              color: fieldState.error ? "var(--red-800)" : "primary.main",
              "&.Mui-checked": {
                color: fieldState.error ? "var(--red-800)" : "primary.main",
              },
              border: "none",
              height: "auto",
              width: "auto",
            }}
          />
        }
        label={label}
        sx={{
          margin: 0,
          "& .MuiFormControlLabel-label": {
            fontSize: { xs: "16px", md: "18px", xl: "20px" },
            color: disabled ? "var(--gray-600)" : "var(--gray-900)",
          },
        }}
      />
    );
  },
);

CheckboxField.displayName = "CheckboxField";

// Error Message Component
const ErrorMessage = memo(
  ({ fieldState, name }: { fieldState: { error?: FieldError }; name: string }) => {
    if (!fieldState.error) {
      return <Typography />;
    }

    return (
      <FormHelperText
        id={`${name}-error`}
        error
        sx={{
          mb: "6px",
          color: "var(--red-800)",
          textAlign: "left",
          fontSize: { xs: "16px", md: "18px", xl: "20px" },
        }}
      >
        {fieldState.error.message}
      </FormHelperText>
    );
  },
);

ErrorMessage.displayName = "ErrorMessage";

/**
 * FormRow Component
 *
 * Renders a row in a form, including a label, input field, and error message.
 * Supports various input types: text, select, date, email, password, autocomplete, number, and checkbox.
 * Integrates with react-hook-form for form state management.
 * @template T - The type of form field values.
 * @param {FormRowProps<T>} props - The props for the FormRow component.
 * Supported Types:
 * - text, email, password: Standard text inputs
 * - number: Integer number input (only digits 0-9)
 * - number-float: Decimal number input (digits 0-9 and decimal point)
 * - select: Dropdown selection with options
 * - date: Date picker with formatting
 * - autocomplete: Searchable dropdown with freeSolo option
 * - checkbox: Boolean checkbox with integrated label
 */
function FormRow<T extends FieldValues = FieldValues>({
  label,
  sxLabel,
  sxBoxField,
  sxField,
  sxBoxRow,
  checkboxLabel,
  name,
  disabled = false,
  control,
  rules,
  options,
  type = "text",
  size = "medium",
  autoCompleteAllowAny = false,
  formatDate = DASH_FORMAT_DATE,
  onChange,
}: FormRowProps<T>) {
  const renderField = useCallback(
    (field: ControllerRenderProps<T, any>, fieldState: { error?: FieldError }) => {
      const commonProps = {
        field: field as ControllerRenderProps<FieldValues, any>,
        fieldState,
        size,
        disabled,
        sxField,
        onChange,
      };

      switch (type) {
        case "select":
          return <SelectField {...commonProps} options={options || []} />;

        case "date":
          return <DateField {...commonProps} formatDate={formatDate} />;

        case "autocomplete":
          return (
            <AutocompleteField
              {...commonProps}
              options={options || []}
              autoCompleteAllowAny={autoCompleteAllowAny}
            />
          );

        case "checkbox":
          return <CheckboxField {...commonProps} label={!checkboxLabel ? label : ""} />;

        default:
          return <TextFieldComponent {...commonProps} type={type} />;
      }
    },
    [type, options, size, disabled, sxField, onChange, formatDate, autoCompleteAllowAny, label],
  );

  // Checkbox layout is different - no separate label
  const isCheckbox = type === "checkbox";

  if (type === "label") {
    return (
      <LabelHeader
        label={label}
        sxBox={{
          mb: "2px",
          pl: { xs: "16px", lg: "32px" },
          height: "48px",
          backgroundColor: "var(--silver-150)",
          boxSizing: "border-box",
          ...sxBoxRow,
        }}
      />
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          mb: 0.25,
          "& > *": { flexShrink: 0 },
          ...sxBoxRow,
        }}
      >
        {/* Label - Skip for checkbox as it's included in the checkbox component */}
        {(!isCheckbox || checkboxLabel) && <LabelHeader label={label} sxBox={{ ...sxLabel }} />}
        {isCheckbox && !checkboxLabel && <Box sx={{ ...sxLabel, p: 0 }} />}

        {/* Input */}
        <Box
          sx={{
            flexGrow: 1,
            ...sxBoxField,
          }}
        >
          <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => renderField(field, fieldState)}
          />
        </Box>
      </Box>

      {/* Error Message */}
      <Box
        sx={{
          ml: isCheckbox ? 0 : "auto",
          ...sxBoxField,
        }}
      >
        <Controller
          name={name}
          control={control}
          render={({ fieldState }) => <ErrorMessage fieldState={fieldState} name={name} />}
        />
      </Box>
    </>
  );
}

export default memo(FormRow) as typeof FormRow;
